import React, { useCallback } from 'react';
import type * as CSS from 'csstype';
import { CanvasEntityDisabledReason } from '@elyx-code/project-logic-tree';
import './expand-properties-icon-button.css';
import { DraggableObject } from '../../../lib/canvas';
import { useCanvasPopup } from '../../../services/editor/CanvasPopup';
import CanvasPopupBaseComponent from '../../../lib/popup/CanvasPopupBaseComponent';
import { Placement } from '@floating-ui/dom';
import { EditorService } from '../../../services/editor';

interface IExpandPropertiesIconButtonProps {
	draggableObject: DraggableObject;
	id: string;
	onClick: (e: MouseEvent) => void;
	expanded: boolean;
	tooltip?: string;
	tooltipPlacement?: Placement;
	parentDisabledReasons: CanvasEntityDisabledReason[];
	style?: CSS.Properties;
	project: EditorService;
}

export default function ExpandPropertiesIconButton(
	props: IExpandPropertiesIconButtonProps
) {
	const memoizedCanvasPopupBody = useCallback(
		() => (
			<CanvasPopupBaseComponent
				containerClasses={['tooltip', 'tooltip--large-font']}
				id={props.id + '--popup--body'}
			>
				{props.tooltip || 'Click to expand'}
			</CanvasPopupBaseComponent>
		),
		[props.tooltip, props.id],
	);

	const canvasPopup = useCanvasPopup(
		{
			editor: props.project,
			id: props.id + '--popup',
			anchorQuerySelector: '#' + props.id,
			arrowClasses: ['tooltip-arrow'],
			parentCanvasObject: props.draggableObject,
			placement: props.tooltipPlacement || 'right',
			arrowContainerSelector: '#' + props.id + '--popup--body',
		},
		memoizedCanvasPopupBody,
	)
		.setupHover({
			closeDelay: 80,
			openDelay: 600,
		})
		.onBeforeShow(() => {
			if (!!props.parentDisabledReasons.length) {
				return false;
			}

			return true;
		});

	return (
		<button
			className='expand-properties-icon-button'
			disabled={!!props.parentDisabledReasons.length}
			onClick={(e) => {
				canvasPopup?.hide();

				props.onClick(e as any);
			}}
			style={props.style}
			id={props.id}
			ref={canvasPopup.anchorRef}
		>
			<i
				className={`fa-regular ${
					props.expanded
						? 'fa-arrows-to-dotted-line'
						: 'fa-arrows-from-dotted-line'
				}`}
			/>
		</button>
	);
}
